/*eslint max-lines-per-function: ["error", 300]*/
import React, { useCallback, useState } from 'react';
import { Card, Tab } from 'react-bootstrap';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { Role } from '../../../common/spdCore/authorization/roles';
import { Filter } from '../RegistersView/Filter';
import { ReportConsultationsSearch } from '../../components/Reports/ReportConsultationsSearch';
import { TabsHistory } from '../../../app/components/TabsHistory';

enum ReportConsultationsTabs {
  WITH_HEALTHCARE_CONSULTATION = 'Wg lek./psych., placówki i rodzaju konsultacji',
  WITH_HEALTHCARE = 'Wg lek./psych. i placówki',
  WITH_CONSULTATION = 'Wg lek./psych. i rodzaju konsultacji',
  WITH_PSYCHOLOGIST = 'Wg lek./psych.',
}

export const ReportConsultationsView: React.FC = () => {
  const spd = useSpdCore();
  const [userUid, setUserUid] = useState<string | undefined>();
  const [companyId, setCompanyId] = useState<string | undefined>();
  const [dateFrom, setDateFrom] = useState<Date | undefined>(
    new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0),
  );
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [healthcareFacilityId, setHealthcareFacilityId] = useState<
    string | undefined
  >();

  const getDataWithHealthcareConsultation = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) =>
      spd.reports.getConsultationsByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        true,
        true,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getDataWithHealthcare = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) =>
      spd.reports.getConsultationsByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        true,
        false,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getDataWithConsultation = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) =>
      spd.reports.getConsultationsByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        false,
        true,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getDataWithPsychologist = useCallback(
    (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) =>
      spd.reports.getConsultationsByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        false,
        false,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getCountWithHealthcareConsultation = useCallback(
    (filterText?: string) =>
      spd.reports.getConsultationsCount(
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        true,
        true,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getCountWithHealthcare = useCallback(
    (filterText?: string) =>
      spd.reports.getConsultationsCount(
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        true,
        false,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getCountWithConsultation = useCallback(
    (filterText?: string) =>
      spd.reports.getConsultationsCount(
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        false,
        true,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  const getCountActive = useCallback(
    (filterText?: string) =>
      spd.reports.getConsultationsCount(
        filterText,
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        false,
        false,
      ),
    [spd.reports, dateFrom, dateTo, userUid, companyId, healthcareFacilityId],
  );

  return (
    <>
      <Card>
        <Card.Header>{'Zestawienie konsultacji'}</Card.Header>
        <Card.Body>
          <Filter
            headerName={'Zestawienie konsultacji'}
            roles={[
              Role.MedicalAssessor,
              Role.MedicalSpecialist,
              Role.PsychologistAssessor,
              Role.PsychologistSpecialist,
            ]}
            withUser={true}
            withDate={true}
            withHealthcareFacility={true}
            withCompany={true}
            userName={'Psycholog / Lekarz'}
            onChangeUserUid={setUserUid}
            onChangeCompanyId={setCompanyId}
            onChangeDateFrom={setDateFrom}
            onChangeDateTo={setDateTo}
            onChangeHealthcareFacilityId={setHealthcareFacilityId}
            canClean={true}
            emptyOnStart={true}
          />
          <TabsHistory
            activeKey={ReportConsultationsTabs.WITH_HEALTHCARE_CONSULTATION}
            id="product"
            className="mb-3"
            mountOnEnter
            unmountOnExit
          >
            <Tab
              eventKey={ReportConsultationsTabs.WITH_HEALTHCARE_CONSULTATION}
              title={ReportConsultationsTabs.WITH_HEALTHCARE_CONSULTATION}
              className="m-3"
            >
              <ReportConsultationsSearch
                getData={getDataWithHealthcareConsultation}
                getCount={getCountWithHealthcareConsultation}
                withHealthcareFacility={true}
                withType={true}
              />
            </Tab>
            <Tab
              eventKey={ReportConsultationsTabs.WITH_HEALTHCARE}
              title={ReportConsultationsTabs.WITH_HEALTHCARE}
              className="m-3"
            >
              <ReportConsultationsSearch
                getData={getDataWithHealthcare}
                getCount={getCountWithHealthcare}
                withHealthcareFacility={true}
                withType={false}
              />
            </Tab>
            <Tab
              eventKey={ReportConsultationsTabs.WITH_CONSULTATION}
              title={ReportConsultationsTabs.WITH_CONSULTATION}
              className="m-3"
            >
              <ReportConsultationsSearch
                getData={getDataWithConsultation}
                getCount={getCountWithConsultation}
                withHealthcareFacility={false}
                withType={true}
              />
            </Tab>
            <Tab
              eventKey={ReportConsultationsTabs.WITH_PSYCHOLOGIST}
              title={ReportConsultationsTabs.WITH_PSYCHOLOGIST}
              className="m-3"
            >
              <ReportConsultationsSearch
                getData={getDataWithPsychologist}
                getCount={getCountActive}
                withHealthcareFacility={false}
                withType={false}
              />
            </Tab>
          </TabsHistory>
        </Card.Body>
      </Card>
    </>
  );
};
